import { Box, Button, Icon, Link, PancakeStack, Separator, Sheet, Text } from '@betterment/samba-react';
import { format } from 'date-fns';

import { DownloadErrorBanner } from './DownloadErrorBanner';
import { useFileDownload } from './useFileDownload';

import { BulkFeeIntentStatus } from '../../../../graphql/schema';
import { BulkFeeIntentProps } from '../data';

const SuccessfulContent: React.FC<BulkFeeIntentProps> = ({ bulkFeeIntent }) => {
  const pdfStatementUrl = `/api/v1/bulk_fee_intent_exports/${bulkFeeIntent.id}/pdf_statement.pdf`;
  const csvStatementUrl = `/api/v1/bulk_fee_intent_exports/${bulkFeeIntent.id}/csv_statement.csv`;
  const { hasDownloadError, handleDownload } = useFileDownload();

  return (
    <>
      <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>Paid to firm on</Text>
        <Text>{format(new Date(bulkFeeIntent.paidToFirmOn!), 'MMM d, yyyy')}</Text>
      </Box>
      <Separator />
      <Text>Completed fees ({bulkFeeIntent.submittedCount - bulkFeeIntent.failedCount!})</Text>
      <Box css={{ display: 'flex', alignItems: 'center' }}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          href="#"
          variant="textPrimary"
          onClick={e => {
            e.preventDefault();
            void handleDownload(pdfStatementUrl, 'statement.pdf');
          }}
          css={{ cursor: 'pointer' }}
        >
          <Box css={{ display: 'inline-flex', alignItems: 'center' }}>
            <Icon name="DocumentPopulated" size="small" />
            <Text>See statement</Text>
          </Box>
        </Link>
        <Text css={{ px: 'space.100', position: 'relative', bottom: '3px' }}>|</Text>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          href="#"
          variant="textPrimary"
          onClick={e => {
            e.preventDefault();
            void handleDownload(csvStatementUrl, 'statement.csv');
          }}
          css={{ cursor: 'pointer' }}
        >
          <Box css={{ display: 'inline-flex', alignItems: 'center' }}>
            <Icon name="Download" size="small" />
            <Text>Download CSV</Text>
          </Box>
        </Link>
      </Box>
      {hasDownloadError && <DownloadErrorBanner resource="statement" />}
    </>
  );
};

const FailedContent: React.FC<BulkFeeIntentProps> = ({ bulkFeeIntent }) => {
  const failedCsvUrl = `/api/v1/bulk_fee_intent_exports/${bulkFeeIntent.id}/failure_csv.csv`;
  const { hasDownloadError, handleDownload } = useFileDownload();

  return (
    <>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.050' }}>
        <Text>Failed fees ({bulkFeeIntent.failedCount})</Text>
        <Text color="text.weak" variant="text50">
          See CSV for failure reasons.
        </Text>
      </Box>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        href="#"
        variant="textPrimary"
        onClick={e => {
          e.preventDefault();
          void handleDownload(failedCsvUrl, 'failed-fees.csv');
        }}
        css={{ cursor: 'pointer' }}
      >
        <Box css={{ display: 'inline-flex', alignItems: 'center' }}>
          <Icon name="Download" size="small" />
          <Text>Download CSV</Text>
        </Box>
      </Link>
      {hasDownloadError && <DownloadErrorBanner resource="download" />}
    </>
  );
};

const ProcessingContent: React.FC<BulkFeeIntentProps> = ({ bulkFeeIntent }) => {
  const uploadCsvUrl = `/api/v1/bulk_fee_intent_exports/${bulkFeeIntent.id}/upload_csv.csv`;
  const { hasDownloadError, handleDownload } = useFileDownload();

  return (
    <>
      <Text>Processing fees ({bulkFeeIntent.submittedCount})</Text>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        href="#"
        variant="textPrimary"
        onClick={e => {
          e.preventDefault();
          void handleDownload(uploadCsvUrl, bulkFeeIntent.uploadedCsvFilename || 'upload.csv');
        }}
        css={{ cursor: 'pointer' }}
      >
        <Box css={{ display: 'flex', alignItems: 'flex-start' }}>
          <Box>
            <Icon name="Download" size="small" />
          </Box>
          <Text>{bulkFeeIntent.uploadedCsvFilename}</Text>
        </Box>
      </Link>
      {hasDownloadError && <DownloadErrorBanner resource="download" />}
    </>
  );
};

export const BulkFeeIntentSheet: React.FC<BulkFeeIntentProps> = ({ bulkFeeIntent }) => {
  const shouldDisplayProcessing = bulkFeeIntent.status === BulkFeeIntentStatus.Processing;
  const shouldDisplaySuccess =
    bulkFeeIntent.status === BulkFeeIntentStatus.Completed &&
    bulkFeeIntent.submittedCount !== bulkFeeIntent.failedCount!;
  const shouldDisplayFailure = bulkFeeIntent.status === BulkFeeIntentStatus.Completed && bulkFeeIntent.failedCount! > 0;

  return (
    <PancakeStack
      css={{ maxWidth: 'container.medium', p: 'space.400', pb: 'space.0', mx: '0', overflowWrap: 'break-word' }}
    >
      <PancakeStack.Header css={{ maxWidth: 'inherit', overflowWrap: 'break-word', pb: 'space.300', minWidth: 0 }}>
        <Sheet.Title>{bulkFeeIntent.name}</Sheet.Title>
      </PancakeStack.Header>
      <PancakeStack.Main css={{ flex: 1, overflowY: 'auto' }}>
        <Box
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 'space.300',
            mb: 'space.300',
          }}
        >
          <Text>Submitted on</Text>
          <Text>{format(new Date(bulkFeeIntent.createdAt), 'MMM d, yyyy')}</Text>
        </Box>
        <Box>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.300' }}>
            <Separator />
            {shouldDisplayProcessing && <ProcessingContent bulkFeeIntent={bulkFeeIntent} />}
            {shouldDisplaySuccess && <SuccessfulContent bulkFeeIntent={bulkFeeIntent} />}
            {shouldDisplaySuccess && shouldDisplayFailure && <Separator />}
            {shouldDisplayFailure && <FailedContent bulkFeeIntent={bulkFeeIntent} />}
          </Box>
        </Box>
      </PancakeStack.Main>
      <PancakeStack.Footer css={{ mb: 'space.300' }}>
        <Separator css={{ mb: 'space.300' }} />
        <Sheet.Close asChild={true}>
          <Button variant="solidSecondary">Close</Button>
        </Sheet.Close>
      </PancakeStack.Footer>
    </PancakeStack>
  );
};
